/**
 * Page Repository
 * Data access for pages
 * 
 * @module repositories/pageRepository
 */

import { supabase } from '../services/supabase';
import { ApiError } from '../lib/errors';
import { logger } from '../lib/logger';
import { ViewState, CanvasBackground, Stroke } from '../types';

// =============================================================================
// Types
// =============================================================================

export interface PageRow {
    id: string;
    board_id: string;
    user_id: string;
    name: string;
    icon: string | null;
    color: string | null;
    order: number;
    view_state: ViewState | null;
    background: CanvasBackground | null;
    strokes: Stroke[] | null;
    version: number;
    created_at: string;
    updated_at: string;
}

export interface PageInsert {
    id: string;
    board_id: string;
    user_id: string;
    name: string;
    icon?: string;
    color?: string;
    order?: number;
    view_state?: ViewState;
    background?: CanvasBackground;
    version?: number;
}

export interface PageUpdate {
    name?: string;
    icon?: string;
    color?: string;
    order?: number;
    view_state?: ViewState;
    background?: CanvasBackground;
    version?: number;
}

// =============================================================================
// Repository
// =============================================================================

export const pageRepository = {
    /**
     * Find all pages for a board
     */
    async findByBoardId(boardId: string): Promise<PageRow[]> {
        logger.debug('[PageRepo] findByBoardId', { boardId });

        const { data, error } = await supabase
            .from('pages')
            .select('*')
            .eq('board_id', boardId)
            .order('order', { ascending: true });

        if (error) {
            throw new ApiError('Failed to fetch pages', {
                statusCode: 500,
                context: { boardId, error: error.message },
            });
        }

        return data ?? [];
    },

    /**
     * Find page by ID
     */
    async findById(id: string): Promise<PageRow | null> {
        logger.debug('[PageRepo] findById', { id });

        const { data, error } = await supabase
            .from('pages')
            .select('*')
            .eq('id', id)
            .single();

        if (error) {
            if (error.code === 'PGRST116') {
                return null;
            }
            throw new ApiError('Failed to fetch page', {
                statusCode: 500,
                context: { id, error: error.message },
            });
        }

        return data;
    },

    /**
     * Create a new page
     */
    async create(page: PageInsert): Promise<PageRow> {
        logger.debug('[PageRepo] create', { boardId: page.board_id, name: page.name });

        const { data, error } = await supabase
            .from('pages')
            .insert({
                ...page,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            })
            .select()
            .single();

        if (error) {
            throw new ApiError('Failed to create page', {
                statusCode: 500,
                context: { error: error.message },
            });
        }

        return data;
    },

    /**
     * Update a page
     */
    async update(id: string, updates: PageUpdate): Promise<PageRow> {
        logger.debug('[PageRepo] update', { id, fields: Object.keys(updates) });

        const { data, error } = await supabase
            .from('pages')
            .update({
                ...updates,
                updated_at: new Date().toISOString(),
            })
            .eq('id', id)
            .select()
            .single();

        if (error) {
            throw new ApiError('Failed to update page', {
                statusCode: 500,
                context: { id, error: error.message },
            });
        }

        return data;
    },

    /**
     * Delete a page
     */
    async delete(id: string): Promise<void> {
        logger.debug('[PageRepo] delete', { id });

        const { error } = await supabase
            .from('pages')
            .delete()
            .eq('id', id);

        if (error) {
            throw new ApiError('Failed to delete page', {
                statusCode: 500,
                context: { id, error: error.message },
            });
        }
    },

    /**
     * Check if pages exist for a board
     */
    async existsForBoard(boardId: string): Promise<boolean> {
        const { count, error } = await supabase
            .from('pages')
            .select('*', { count: 'exact', head: true })
            .eq('board_id', boardId);

        if (error) {
            logger.error('[PageRepo] existsForBoard check failed', error);
            return false;
        }

        return (count ?? 0) > 0;
    },

    /**
     * Get max order index for a board
     */
    async getMaxOrderIndex(boardId: string): Promise<number> {
        const { data } = await supabase
            .from('pages')
            .select('order')
            .eq('board_id', boardId)
            .order('order', { ascending: false })
            .limit(1)
            .single();

        return data?.order ?? -1;
    },
};
